// src/components/padrekko/qc/QcResolvedPadLayer.jsx
//
// Renders the geometry the current pad's vision analysis is FRAMED on, when
// that is a detected pad outline rather than the analyst's AOI.
//
// Both outlines are on screen together on purpose. aoi_geom is the analyst's
// SCOPE OF INQUIRY and does not stop being it; the resolved geometry is what
// the model was actually shown. Showing only one answers the wrong question —
// "where did I look" and "what was analyzed" are different, and the gap between
// them is the whole point of the feature.
//
// ── THE TWO MUST BE TELLABLE APART IN A SCREENSHOT ──────────────────────────
//
// These get discussed by being pasted into a ticket or dropped in a report, so
// the difference has to survive being a static image, at whatever zoom the
// person happened to be at, possibly in greyscale.
//
// A DASH PATTERN ALONE DOES NOT SURVIVE THAT. At low zoom a dashed 1.5px line
// and a solid one are the same handful of pixels, and the distinction is gone
// exactly when the two outlines are closest together on screen.
//
// A LOW-OPACITY FILL ALONE DOES NOT EITHER, AND THAT WAS MEASURED RATHER THAN
// ASSUMED. Teal at 0.14 over dark soil is a 16/255 greyscale difference — thin
// but there. Over CALICHE, which is what a well pad actually looks like in this
// imagery, it is **0 at every opacity from 0.10 to 0.28**: the tint's luminance
// lands on the caliche's, so in a greyscale screenshot the fill disappears
// precisely over the pads it exists to mark.
//
// So there are THREE cues, and the load-bearing one is the casing:
//
//   1. CASING. A dark line UNDER the teal one. A light/dark edge pair survives
//      greyscale over anything, because it does not depend on the background:
//      measured at a constant 176/255 line-against-casing contrast over dark
//      soil, caliche and bright sand alike, against 16/255 for the fill over
//      caliche. This is the cue that works in a screenshot.
//   2. DASH. Reads when someone is zoomed in and looking closely. The inspector
//      frames to maxZoom 17, where a 200 m AOI is ~198px and an 80 m resolved
//      pad is ~79px, so the dashes are well resolved at the zoom this is
//      actually looked at.
//   3. FILL. Still worth having — it is the clearest cue in COLOUR and over
//      dark ground — but it is no longer the one being relied on.
//
// COLOR IS NOT ONE OF THEM, deliberately. Teal means "pad" across the
// Volumetrics tab and the QC inspector (QcCurrentPadLayer.jsx:12-15), and
// spending that association to distinguish two things that are both the pad
// would cost more than it buys. Same hue, different treatment.
//
// Panel-mounted, same lifecycle as QcCurrentPadLayer: tab switch unmounts and
// cleanup removes the layers. Hides during drawingPolygon so the canvas
// snapshot the analyze call sends stays clean of it — the model must not be
// shown an outline drawn over its own input.

import { useEffect, useMemo } from "react";
import { useAppViewStore } from "../../../stores/useAppViewStore";
import { useQcStore } from "../../../stores/useQcStore";

const SOURCE_ID       = "qc-resolved-pad";
const FILL_LAYER_ID   = "qc-resolved-pad-fill";
const CASING_LAYER_ID = "qc-resolved-pad-casing";
const LINE_LAYER_ID   = "qc-resolved-pad-line";

// Same hue as the AOI outline (QcCurrentPadLayer.jsx:148) — the distinction is
// carried by casing, dash and fill, not by color.
const TEAL = "#00d4ff";
// Near-black, so the casing reads as an edge rather than as a second colour.
const CASING = "#0b1220";

// Heavier than the AOI outline at every zoom, so the dashes are resolvable
// rather than merging into a dotted smudge when someone zooms out.
const RESOLVED_LINE_WIDTH = [
  "interpolate", ["linear"], ["zoom"],
  10, 1.2,
  14, 2.0,
  17, 2.8,
];

// The casing is the same shape, wider, underneath — so every dash gets a dark
// border on both sides and the outline keeps its edge over any ground tone.
const CASING_LINE_WIDTH = [
  "interpolate", ["linear"], ["zoom"],
  10, 3.2,
  14, 4.5,
  17, 5.8,
];

const DASH = [2, 1.5];

function buildFeatureCollection(pad) {
  // ONLY ON THE RESOLVED ARM. On a fallback the resolved geometry IS the AOI,
  // and drawing a second outline exactly on top of the first would say there
  // are two things here when there is one.
  if (pad?.geometry_source !== "pad_feature" || !pad?.resolved_geom?.type) {
    return { type: "FeatureCollection", features: [] };
  }
  return {
    type: "FeatureCollection",
    features: [{ type: "Feature", geometry: pad.resolved_geom, properties: {} }],
  };
}

export default function QcResolvedPadLayer() {
  const mapInstance    = useAppViewStore((s) => s.mapInstance);
  const session        = useQcStore((s) => s.session);
  const currentPadIdx  = useQcStore((s) => s.currentPadIdx);
  const drawingPolygon = useQcStore((s) => s.drawingPolygon);

  const pad = session?.pads?.[currentPadIdx ?? 0];

  const featureCollection = useMemo(
    () => buildFeatureCollection(pad),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pad?.resolved_geom, pad?.geometry_source]
  );

  // ── 1. mount/unmount ────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapInstance) return;

    if (!mapInstance.getSource(SOURCE_ID)) {
      mapInstance.addSource(SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      // FILL FIRST so the line draws over its own edge.
      mapInstance.addLayer({
        id: FILL_LAYER_ID,
        type: "fill",
        source: SOURCE_ID,
        paint: {
          "fill-color": TEAL,
          // Low enough to read the imagery through — an analyst is judging
          // ground conditions and a tint that hides them defeats the purpose.
          // High enough to be unmistakably a filled region in a screenshot.
          "fill-opacity": 0.16,
        },
      });
      // CASING UNDER THE LINE. Order matters: casing, then line on top.
      mapInstance.addLayer({
        id: CASING_LAYER_ID,
        type: "line",
        source: SOURCE_ID,
        paint: {
          "line-color": CASING,
          "line-width": CASING_LINE_WIDTH,
          "line-dasharray": DASH,
          "line-opacity": 0.85,
        },
      });
      mapInstance.addLayer({
        id: LINE_LAYER_ID,
        type: "line",
        source: SOURCE_ID,
        paint: {
          "line-color": TEAL,
          "line-width": RESOLVED_LINE_WIDTH,
          "line-dasharray": DASH,
        },
      });
    }

    return () => {
      if (!mapInstance || !mapInstance.style) return;
      for (const id of [LINE_LAYER_ID, CASING_LAYER_ID, FILL_LAYER_ID]) {
        if (mapInstance.getLayer(id)) mapInstance.removeLayer(id);
      }
      if (mapInstance.getSource(SOURCE_ID)) mapInstance.removeSource(SOURCE_ID);
    };
  }, [mapInstance]);

  // ── 2. source data sync ─────────────────────────────────────────────────
  useEffect(() => {
    const src = mapInstance?.getSource?.(SOURCE_ID);
    if (!src) return;
    src.setData(featureCollection);
  }, [mapInstance, featureCollection]);

  // ── 3. visibility ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapInstance) return;
    const vis = drawingPolygon ? "none" : "visible";
    for (const id of [FILL_LAYER_ID, CASING_LAYER_ID, LINE_LAYER_ID]) {
      if (mapInstance.getLayer(id)) mapInstance.setLayoutProperty(id, "visibility", vis);
    }
  }, [mapInstance, drawingPolygon]);

  return null;
}
